import { useQuery } from "@apollo/client";
import { Alert, Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import React from "react";

import { CAPTURED_THREAD, type CapturedMessageItem, campaignLabel, relativeTime } from "./helpers";

interface Props {
  memberId: string;
  memberLabel: string;
  days: number;
}

/**
 * One member's DM thread, read like a chat: what we sent on the right, what she
 * replied on the left.
 *
 * Read-only on purpose. This is the archive — replying happens in Stream, and a
 * send button here would quietly become a fifth outreach tool that skips the
 * recently-messaged guard.
 */
const ThreadView: React.FC<Props> = ({ memberId, memberLabel, days }) => {
  const { data, loading, error } = useQuery<{ adminCapturedThread: CapturedMessageItem[] }>(
    CAPTURED_THREAD,
    { variables: { memberId, days }, fetchPolicy: "network-only" },
  );

  const messages = data?.adminCapturedThread ?? [];

  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%", overflow: "auto" }}>
      <Typography variant="subtitle2" gutterBottom>
        {memberLabel}
      </Typography>

      {loading && <CircularProgress size={20} />}

      {/* Distinguish a broken query from a genuinely empty thread. */}
      {error && <Alert severity="error">Couldn't load this thread: {error.message}</Alert>}

      {!loading && !error && messages.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nothing captured for her in the last {days} days.
        </Typography>
      )}

      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {messages.map((message) => (
          <Box
            key={message.messageId}
            sx={{
              alignSelf: message.fromOperator ? "flex-end" : "flex-start",
              maxWidth: "80%",
              bgcolor: message.fromOperator ? "primary.main" : "action.hover",
              color: message.fromOperator ? "primary.contrastText" : "text.primary",
              borderRadius: 2,
              px: 1.5,
              py: 1,
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {message.text}
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.75, display: "block", mt: 0.5, textAlign: "right" }}
            >
              {message.fromOperator ? "us" : "her"} · {relativeTime(message.sentAt)}
              {/* On a reply this is what she was answering, not what she sent. */}
              {message.campaign ? ` · ${campaignLabel(message.campaign)}` : ""}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default ThreadView;
